import { useQueries } from '@tanstack/react-query'
import { toast } from 'sonner'
import z from 'zod'
import { TEN_MINUTES } from '~/features/storyous/constants'
import { useHandleMissingToken } from '~/features/storyous/hooks/useHandleMissingToken'

const stockMenuSchema = z.array(
  z.object({
    items: z.array(
      z.object({
        name: z.string(),
        placeValues: z.object({
          priceLevels: z.object({ default: z.object({ price: z.number() }) }),
        }),
      })
    ),
  })
)

type StockMenu = z.TypeOf<typeof stockMenuSchema>

const listResponseSchema = z.object({
  nextPage: z.string().nullable().optional(),
  items: stockMenuSchema.default([]),
})

const normalizeMenu = (stock: StockMenu[number]['items'], chosenMerchant: 'wollem' | 'mandarin') =>
  stock.map((s) => ({
    name: s.name,
    price: s.placeValues.priceLevels.default.price,
    store: chosenMerchant,
  }))

export const useGetStoryousMenu = ({
  token,
  merchants,
}: {
  token: string | undefined
  merchants: Array<'wollem' | 'mandarin'>
}) => {
  const handleMissingToken = useHandleMissingToken()
  return useQueries({
    queries: merchants.map((chosenMerchant) => ({
      queryKey: ['get-storyous-menu', token, chosenMerchant],
      queryFn: async () => {
        if (!token) {
          toast.warning('Can not make the request. Auth token is missing')
          throw Error('Can not make the request. Auth token is missing')
        }

        const response = await fetch(`/api/storyous_menu`, {
          method: 'POST',
          body: JSON.stringify({ token, chosenMerchant }),
        })

        if (handleMissingToken(response)) return null

        const result = await response.json()
        if (!response.ok) {
          toast.error('Storyous menu could not be fetched.')
          console.error(result)
          throw result
        }

        const parsed = listResponseSchema.safeParse(result)

        if (!parsed.success) {
          console.warn(parsed.error)
          toast.error('Unexpected return from Storyous. Check console.')
          return null
        }

        return normalizeMenu(
          parsed.data.items.flatMap((m) => m.items),
          chosenMerchant
        )
      },
      staleTime: TEN_MINUTES,
      enabled: !!token,
    })),
  })
}
